import { useMemo } from 'react';
import { rotateSize } from '../utils/transforms';
import { ImageState } from '../model/types';

type ImageTransformParams = {
  image: HTMLImageElement | null;
  containerSize: { width: number; height: number };
  modifications: ImageState;
  fitContainer?: boolean;
};

export const useImageTransform = ({
  image,
  containerSize,
  modifications,
  fitContainer = false,
}: ImageTransformParams) => {
  return useMemo(() => {
    if (!image || containerSize.width === 0 || containerSize.height === 0) {
      return null;
    }

    const { isFlipped, rotationAngleDegree, normalizedCropRect } =
      modifications;

    if (fitContainer) {
      const rotatedSize = rotateSize(
        image.width,
        image.height,
        rotationAngleDegree || 0
      );
      const aspect = image.width / image.height;
      const containerAspect = containerSize.width / containerSize.height;

      const normalizedCropRectWithFlip = isFlipped
        ? {
          ...normalizedCropRect,
          xmin: 1 - normalizedCropRect.xmax,
          xmax: 1 - normalizedCropRect.xmin,
        }
        : normalizedCropRect;

      let scale: number;
      if (aspect > containerAspect) {
        scale = containerSize.height / image.height;
      } else {
        scale = containerSize.width / image.width;
      }
      const normXDiff =
        normalizedCropRectWithFlip.xmax - normalizedCropRectWithFlip.xmin;
      const normYDiff =
        normalizedCropRectWithFlip.ymax - normalizedCropRectWithFlip.ymin;
      const imageWidth = rotatedSize.width;
      const imageHeight = rotatedSize.height;
      const groupWidth = containerSize.width;
      const groupHeight = containerSize.height;
      const scaleX = groupWidth / (imageWidth * normXDiff);
      const scaleY = groupHeight / (imageHeight * normYDiff);
      const normCenterX = normalizedCropRectWithFlip.xmin + normXDiff / 2;
      const normCenterY = normalizedCropRectWithFlip.ymin + normYDiff / 2;
      const imageX = -(normCenterX - 0.5) * imageWidth * scaleX;
      const imageY = (normCenterY - 0.5) * imageHeight * scaleY;

      return {
        groupWidth,
        groupHeight,
        imageX,
        imageY,
        imageScale: scale,
        rotationAngleDegree,
        scaleX,
        scaleY,
      };
    }

    const containerMinSize = Math.min(
      containerSize.width,
      containerSize.height
    );
    const rotatedSize = rotateSize(
      image.width,
      image.height,
      rotationAngleDegree || 0
    );

    // Инвертируем x-координаты при отражении
    const normalizedCropRectWithFlip = isFlipped
      ? {
        ...normalizedCropRect,
        xmin: 1 - normalizedCropRect.xmax,
        xmax: 1 - normalizedCropRect.xmin,
      }
      : normalizedCropRect;

    const normXDiff =
      normalizedCropRectWithFlip.xmax - normalizedCropRectWithFlip.xmin;
    const normYDiff =
      normalizedCropRectWithFlip.ymax - normalizedCropRectWithFlip.ymin;

    const imageWidth = rotatedSize.width;
    const imageHeight = rotatedSize.height;

    const aspect = imageWidth / imageHeight;
    const maxImageNormDiff = Math.max(normXDiff * aspect, normYDiff);

    const groupWidth =
      (containerMinSize * normXDiff * aspect) / maxImageNormDiff;
    const groupHeight = (containerMinSize * normYDiff) / maxImageNormDiff;

    const imageScale = groupWidth / (imageWidth * normXDiff);

    const normCenterX = normalizedCropRectWithFlip.xmin + normXDiff / 2;
    const normCenterY = normalizedCropRectWithFlip.ymin + normYDiff / 2;

    const imageX =
      -(normCenterX - 0.5) * imageWidth * imageScale + 0.5 * groupWidth;
    const imageY =
      (normCenterY - 0.5) * imageHeight * imageScale + 0.5 * groupHeight;

    return {
      groupWidth,
      groupHeight,
      imageX,
      imageY,
      imageScale,
      rotationAngleDegree,
    };
  }, [image, containerSize, modifications, fitContainer]);
};
